<template>
  <div class="aciding-report-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Button & Title -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            class="back-btn mr-3"
            @click="goBack"
            title="Back to Acidifying"
          >
            <v-icon color="#0f172a" size="20">mdi-arrow-left</v-icon>
          </v-btn>
          <div class="header-icon-box mr-3">
            <v-icon color="#9a6b27" size="24">mdi-flask-round-bottom-outline</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">ACIDIFYING &amp; REFINING REGISTER</span>
              <span class="badge-aciding-count">
                {{ totalRecordsCount }} Batches
              </span>
            </div>
            <div class="page-subtitle">
              Batch-wise old gold refining, outbound dispatch, inbound pure thankam recovery &amp; refining yield
            </div>
          </div>
        </div>

        <!-- Right: Actions (Refresh, CSV, Print, PDF) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            icon
            small
            title="Refresh Report"
            class="refresh-icon-btn"
            :loading="appLoading"
            @click="getList"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#475569"
            class="btn-secondary text-capitalize"
            @click="exportToCSV"
            :disabled="filteredList.length === 0"
          >
            <v-icon left small color="#475569">mdi-file-delimited-outline</v-icon>
            Export CSV
          </v-btn>

          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outline text-capitalize"
            @click="printReport"
          >
            <v-icon left small color="#9a6b27">mdi-printer-outline</v-icon>
            Print
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            :loading="pdfLoading"
            @click="downloadPDF"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Batches -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">REFINING BATCHES</div>
          <div class="kpi-value">{{ kpiMetrics.totalBatches }}</div>
          <div class="kpi-hint">
            <span><b>{{ kpiMetrics.completedCount }}</b> completed</span>
            <span class="mx-1">&bull;</span>
            <span><b>{{ kpiMetrics.pendingCount }}</b> in progress</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="22" color="#2563eb">mdi-cube-unfolded</v-icon>
        </div>
      </div>

      <!-- 2. Total Outbound Dispatched (Net Wt) -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">OUTBOUND (OLD GOLD)</div>
          <div class="kpi-value text-amber">{{ kpiMetrics.totalOutboundNet.toFixed(3) }} <span class="unit-text">gm</span></div>
          <div class="kpi-hint">
            <span>Old gold sent for refining</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #b45309;">
          <v-icon size="22" color="#b45309">mdi-tray-arrow-up</v-icon>
        </div>
      </div>

      <!-- 3. Total Inbound Recovered (Thankam) -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">INBOUND (THANKAM RECOVERED)</div>
          <div class="kpi-value text-emerald">{{ kpiMetrics.totalInboundThankam.toFixed(3) }} <span class="unit-text">gm</span></div>
          <div class="kpi-hint">
            <v-icon x-small color="#10b981" class="mr-1">mdi-gold</v-icon>
            <span>Pure 24K thankam received</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #15803d;">
          <v-icon size="22" color="#15803d">mdi-tray-arrow-down</v-icon>
        </div>
      </div>

      <!-- 4. Average Yield % -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">REFINING YIELD / PURITY</div>
          <div class="kpi-value gold-val">{{ kpiMetrics.yieldPercentage.toFixed(2) }}<span class="unit-text">%</span></div>
          <div class="kpi-hint">
            <v-icon x-small color="#9a6b27" class="mr-1">mdi-percent-outline</v-icon>
            <span>Avg Thankam / Old Gold conversion</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #faf5ff; color: #7c3aed;">
          <v-icon size="22" color="#7c3aed">mdi-chart-timeline-variant-shimmer</v-icon>
        </div>
      </div>
    </div>

    <!-- Filter & Search Toolbar Card -->
    <v-card flat class="filter-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
        <!-- Left: Search Box -->
        <div class="search-input-wrap">
          <v-text-field
            v-model="keyword"
            background-color="#f8fafc"
            color="#9a6b27"
            clearable
            hide-details
            placeholder="Search Batch Code or Acidifier..."
            outlined
            dense
            class="custom-search-field"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <!-- Middle: Status Filter Pills -->
        <div class="d-flex align-center flex-wrap" style="gap: 6px;">
          <v-btn
            small
            depressed
            :class="['preset-btn', !status ? 'preset-active' : '']"
            @click="status = ''"
          >
            All Status
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', status === 'Inbound' || status === 'Acidified' ? 'preset-active' : '']"
            @click="status = 'Inbound'"
          >
            <v-icon x-small left color="#15803d">mdi-check-circle-outline</v-icon>
            Inbound (Completed)
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', status === 'Outbound' || status === 'Batched' ? 'preset-active' : '']"
            @click="status = 'Outbound'"
          >
            <v-icon x-small left color="#b45309">mdi-clock-outline</v-icon>
            Outbound (In Progress)
          </v-btn>
        </div>

        <!-- Acidifier / Supplier Dropdown -->
        <div style="width: 170px;">
          <v-autocomplete
            v-model="sName"
            :items="supplierList"
            item-text="supplierName"
            item-value="_id"
            clearable
            outlined
            hide-details
            dense
            background-color="#f8fafc"
            label="Filter Acidifier"
            class="custom-search-field"
          ></v-autocomplete>
        </div>

        <!-- Date Range Pickers -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                label="From Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              no-title
              scrollable
              color="#9a6b27"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>

          <span class="text-muted" style="font-size: 12px;">to</span>

          <v-menu
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                label="To Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              no-title
              scrollable
              color="#9a6b27"
              @input="menu2 = false"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- Reset Button -->
        <v-btn
          v-if="keyword || status || sName"
          small
          text
          color="#dc2626"
          class="text-capitalize px-2"
          @click="resetFilters"
        >
          <v-icon x-small left>mdi-filter-off</v-icon>
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Main Data Table Card -->
    <v-card flat class="table-card rounded-lg overflow-hidden">
      <!-- Table Header Bar -->
      <div class="table-header-bar px-4 py-3 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="table-section-title">Refining Batch Records</span>
          <span class="table-count-badge">{{ filteredList.length }} entries</span>
        </div>
        <div class="d-flex align-center" style="gap: 12px;">
          <span class="text-caption text-muted">Page {{ page }} of {{ Pagelength || 1 }}</span>
        </div>
      </div>

      <!-- Vuetify Data Table -->
      <v-data-table
        :headers="headers"
        :items="filteredList"
        :loading="appLoading"
        hide-default-footer
        disable-pagination
        class="pro-aciding-table elevation-0"
      >
        <!-- S.No Column -->
        <template v-slot:item.sno="{ index }">
          <span class="text-muted font-weight-bold">{{ index + 1 + (page - 1) * 20 }}</span>
        </template>

        <!-- Batch Code Column -->
        <template v-slot:item.batchNumber="{ item }">
          <span class="batch-code-badge">
            <v-icon x-small color="#1d4ed8" class="mr-1">mdi-barcode-scan</v-icon>
            {{ item.batchNumber || '-' }}
          </span>
        </template>

        <!-- Aciding Date Column -->
        <template v-slot:item.acidingDate="{ item }">
          <span class="date-text">{{ item.batchDate ? formatDate(item.batchDate) : '-' }}</span>
        </template>

        <!-- Outbound Wt Column -->
        <template v-slot:item.outbound="{ item }">
          <span class="weight-badge outbound-wt font-weight-bold">
            {{ Number(item.totalOldGoldNetWeight || 0).toFixed(3) }} g
          </span>
        </template>

        <!-- Return Date Column -->
        <template v-slot:item.returnDate="{ item }">
          <span class="date-text" v-if="item.acidingOldGoldId && item.acidingOldGoldId.thankamReturnDate">
            {{ formatDate(item.acidingOldGoldId.thankamReturnDate) }}
          </span>
          <span class="text-muted" v-else>Pending Return</span>
        </template>

        <!-- Inbound Thankam Column -->
        <template v-slot:item.inbound="{ item }">
          <span
            v-if="item.acidingOldGoldId && item.acidingOldGoldId.thankam"
            class="weight-badge inbound-wt font-weight-bold text-emerald"
          >
            {{ Number(item.acidingOldGoldId.thankam).toFixed(3) }} g
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Yield Column -->
        <template v-slot:item.yieldRate="{ item }">
          <span v-if="calculateYield(item) !== null" class="yield-badge font-weight-bold">
            {{ calculateYield(item).toFixed(2) }}%
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Acidifier Column -->
        <template v-slot:item.acidifier="{ item }">
          <div class="d-flex align-center" style="gap: 6px;">
            <div class="acidifier-avatar">
              {{ getInitials(item.acidifier && item.acidifier.supplierName ? item.acidifier.supplierName : item.acidifierType) }}
            </div>
            <div>
              <div class="acidifier-name font-weight-bold">
                {{ item.acidifier && item.acidifier.supplierName ? item.acidifier.supplierName : (item.acidifierType || 'Internal') }}
              </div>
              <div class="acidifier-sub text-muted" v-if="item.acidifier && item.acidifier.contactNumber">
                {{ item.acidifier.contactNumber }}
              </div>
            </div>
          </div>
        </template>

        <!-- Status Column -->
        <template v-slot:item.status="{ item }">
          <span v-if="item.status == 'Acidified'" class="status-badge status-completed">
            <v-icon x-small color="#15803d" class="mr-1">mdi-check-circle</v-icon>
            INBOUND (COMPLETED)
          </span>
          <span v-else-if="item.status == 'Batched'" class="status-badge status-pending">
            <v-icon x-small color="#b45309" class="mr-1">mdi-clock-fast</v-icon>
            OUTBOUND (IN PROGRESS)
          </span>
          <span v-else class="status-badge status-default">
            {{ item.status || '-' }}
          </span>
        </template>

        <!-- No Data State -->
        <template v-slot:no-data>
          <div class="empty-table-state py-8 text-center">
            <div class="empty-icon-circle mb-3">
              <v-icon size="32" color="#94a3b8">mdi-flask-empty-outline</v-icon>
            </div>
            <div class="empty-title">No Acidifying Batch Records Found</div>
            <div class="empty-subtitle">
              {{ keyword || status || sName ? 'No records match your active filter criteria. Try resetting filters.' : 'No acidifying batches dispatched during this date period.' }}
            </div>
            <v-btn
              v-if="keyword || status || sName"
              small
              depressed
              color="#9a6b27"
              dark
              class="mt-4 text-capitalize rounded-lg"
              @click="resetFilters"
            >
              <v-icon x-small left>mdi-filter-off</v-icon>
              Clear Filters
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination Bar -->
      <div class="table-pagination-bar px-4 py-3 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="pagination-info">
          Showing <b>{{ filteredList.length }}</b> batch entries
        </div>
        <v-pagination
          v-if="Pagelength > 1"
          v-model="page"
          :length="Pagelength"
          :total-visible="7"
          color="#9a6b27"
          class="custom-pagination"
        ></v-pagination>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AcidingReport",
  data() {
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);

    return {
      appLoading: false,
      pdfLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,

      page: 1,
      Pagelength: 1,
      menu: false,
      menu2: false,

      fromDate: today,
      toDate: today,
      keyword: "",
      status: "",
      sName: "",

      supplierList: [],
      List2: [],

      headers: [
        { text: "S.No.", value: "sno", sortable: false, width: "70px" },
        { text: "Batch Code", value: "batchNumber", sortable: true, width: "130px" },
        { text: "Dispatch Date", value: "acidingDate", sortable: true, width: "130px" },
        { text: "Outbound (g)", value: "outbound", sortable: true, align: "right", width: "130px" },
        { text: "Return Date", value: "returnDate", sortable: true, width: "130px" },
        { text: "Thankam Pure (g)", value: "inbound", sortable: true, align: "right", width: "150px" },
        { text: "Yield %", value: "yieldRate", sortable: false, align: "right", width: "110px" },
        { text: "Acidifier Refinery", value: "acidifier", sortable: false },
        { text: "Batch Status", value: "status", sortable: true, width: "180px" },
      ],
    };
  },
  computed: {
    filteredList() {
      return this.List2 || [];
    },
    totalRecordsCount() {
      return this.List2 ? this.List2.length : 0;
    },
    kpiMetrics() {
      const list = this.List2 || [];
      let totalBatches = list.length;
      let completedCount = 0;
      let pendingCount = 0;
      let totalOutboundNet = 0;
      let totalInboundThankam = 0;

      list.forEach((item) => {
        const outNet = Number(item.totalOldGoldNetWeight || 0);
        totalOutboundNet += outNet;

        if (item.status === "Acidified") {
          completedCount++;
        } else {
          pendingCount++;
        }

        if (item.acidingOldGoldId && item.acidingOldGoldId.thankam) {
          totalInboundThankam += Number(item.acidingOldGoldId.thankam);
        }
      });

      const yieldPercentage = totalOutboundNet > 0 ? (totalInboundThankam / totalOutboundNet) * 100 : 0;

      return {
        totalBatches,
        completedCount,
        pendingCount,
        totalOutboundNet,
        totalInboundThankam,
        yieldPercentage,
      };
    },
  },
  watch: {
    fromDate() {
      this.page = 1;
      this.getList();
    },
    toDate() {
      this.page = 1;
      this.getList();
    },
    page() {
      this.getList();
    },
    status() {
      this.page = 1;
      this.getList();
    },
    keyword() {
      this.page = 1;
      this.getList();
    },
    sName() {
      this.page = 1;
      this.getList();
    },
  },
  mounted() {
    this.getList();
    this.getSupplier();
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/AcidingList");
      }
    },

    resetFilters() {
      this.keyword = "";
      this.status = "";
      this.sName = "";
      this.page = 1;
      this.getList();
    },

    calculateYield(item) {
      const out = Number(item.totalOldGoldNetWeight || 0);
      const thankam = Number(item.acidingOldGoldId && item.acidingOldGoldId.thankam ? item.acidingOldGoldId.thankam : 0);
      if (out > 0 && thankam > 0) {
        return (thankam / out) * 100;
      }
      return null;
    },

    getInitials(name) {
      if (!name) return "AC";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },

    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return String(item);
      const day = dt.getDate();
      const month = dt.toLocaleString("default", { month: "short" });
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },

    getList() {
      this.appLoading = true;
      let queryStatus = this.status;
      if (queryStatus === "Inbound") {
        queryStatus = "Acidified";
      } else if (queryStatus === "Outbound") {
        queryStatus = "Batched";
      }

      axios({
        url: "/oldgoldbatchlist/basedonstatus",
        method: "get",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          count: 20,
          page: this.page,
          sName: this.sName || undefined,
          from: this.fromDate,
          to: this.toDate,
          keyword: this.keyword || undefined,
          status: queryStatus || undefined,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.List2 = response.data.data || [];
            this.Pagelength = Math.ceil((response.data.count || this.List2.length) / 20) || 1;
          } else {
            this.msg = response.data.msg || "Failed to load records";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching aciding report:", err);
        });
    },

    getSupplier() {
      axios({
        method: "POST",
        url: "/stock/supplier/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.supplierList = response.data.supplierlist || [];
          }
        })
        .catch((err) => {
          console.error("Error fetching supplier list:", err);
        });
    },

    printReport() {
      window.print();
    },

    downloadPDF() {
      if (!this.fromDate || !this.toDate) {
        this.msg = "Please choose both dates";
        this.showsnackbar = true;
        return;
      }
      this.pdfLoading = true;
      let queryStatus = this.status;
      if (queryStatus === "Inbound") {
        queryStatus = "Acidified";
      } else if (queryStatus === "Outbound") {
        queryStatus = "Batched";
      }

      axios({
        method: "get",
        url: "/download/acidingreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          sName: this.sName || undefined,
          from: this.fromDate,
          to: this.toDate,
          keyword: this.keyword || undefined,
          status: queryStatus || undefined,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.pdfLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Acidifying_Report_${this.fromDate}_${this.toDate}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.pdfLoading = false;
          this.ServerError = true;
          console.error("Error downloading PDF:", err);
          this.msg = "Failed to download PDF report";
          this.showsnackbar = true;
        });
    },

    exportToCSV() {
      const list = this.List2;
      if (!list || list.length === 0) return;

      const headers = [
        "S.No",
        "Batch Code",
        "Dispatch Date",
        "Outbound Net Wt (g)",
        "Return Date",
        "Inbound Thankam (g)",
        "Yield (%)",
        "Acidifier Refinery",
        "Status"
      ];

      const rows = list.map((item, idx) => {
        const yieldVal = this.calculateYield(item);
        const acidName = item.acidifier && item.acidifier.supplierName ? item.acidifier.supplierName : (item.acidifierType || "Internal");
        const statusText = item.status === "Acidified" ? "INBOUND (COMPLETED)" : "OUTBOUND (IN PROGRESS)";
        return [
          idx + 1,
          `"${item.batchNumber || '-'}"`,
          `"${item.batchDate ? this.formatDate(item.batchDate) : '-'}"`,
          Number(item.totalOldGoldNetWeight || 0).toFixed(3),
          `"${item.acidingOldGoldId && item.acidingOldGoldId.thankamReturnDate ? this.formatDate(item.acidingOldGoldId.thankamReturnDate) : '-'}"`,
          Number(item.acidingOldGoldId && item.acidingOldGoldId.thankam ? item.acidingOldGoldId.thankam : 0).toFixed(3),
          yieldVal !== null ? yieldVal.toFixed(2) : "0.00",
          `"${acidName}"`,
          `"${statusText}"`
        ];
      });

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Acidifying_Report_${this.fromDate}_to_${this.toDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.aciding-report-wrapper {
  padding: 8px 12px;
  background-color: transparent;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.back-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fde047;
}

.page-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.badge-aciding-count {
  font-size: 11px;
  font-weight: 700;
  background: #fefce8;
  color: #b45309;
  border: 1px solid #fef08a;
  border-radius: 6px;
  padding: 2px 8px;
}

.refresh-icon-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.btn-secondary {
  border: 1px solid #cbd5e1 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-gold-outline {
  border: 1px solid #9a6b27 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-primary {
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0 2px;
}

.kpi-value.gold-val {
  color: #9a6b27;
}

.unit-text {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.kpi-icon-pill {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Card */
.filter-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
}

.search-input-wrap {
  flex: 1;
  min-width: 240px;
  max-width: 320px;
}

.custom-search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.custom-search-field :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.custom-search-field :deep(input) {
  font-size: 13px !important;
}

.preset-btn {
  font-size: 11.5px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
  color: #475569 !important;
}

.preset-active {
  background: #9a6b27 !important;
  color: #ffffff !important;
  border-color: #9a6b27 !important;
}

.date-input-field {
  width: 140px;
}

.date-input-field :deep(input) {
  font-size: 12px !important;
}

/* Table Card */
.table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.table-header-bar {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.table-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-count-badge {
  font-size: 11px;
  font-weight: 700;
  background: #e2e8f0;
  color: #334155;
  padding: 1px 6px;
  border-radius: 4px;
}

/* Table Styling */
.pro-aciding-table :deep(th) {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-aciding-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-aciding-table :deep(tr:hover) {
  background-color: #fafbfc !important;
}

.batch-code-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.date-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
}

.weight-badge {
  font-size: 12.5px;
  padding: 2px 8px;
  border-radius: 6px;
  display: inline-block;
}

.outbound-wt {
  background: #fefce8;
  color: #92400e;
  border: 1px solid #fef08a;
}

.inbound-wt {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.yield-badge {
  font-size: 12px;
  background: #faf5ff;
  color: #7c3aed;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e9d5ff;
}

.acidifier-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
}

.acidifier-name {
  font-size: 12.5px;
  color: #0f172a;
}

.acidifier-sub {
  font-size: 11px;
}

/* Status Badges */
.status-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
}

.status-completed {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-pending {
  background: #fefce8;
  color: #b45309;
  border: 1px solid #fef08a;
}

.status-default {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-table-state {
  padding: 40px 20px;
}

.empty-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Utilities */
.text-emerald {
  color: #059669;
}

.text-amber {
  color: #b45309;
}

.text-muted {
  color: #94a3b8;
}
</style>
